'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Sparkles, ShieldCheck } from 'lucide-react';
import { billingApi, publicApi, type BillingPlan } from '@/lib/api';
import { Logo7b } from '@/components/public/auth/ds/Logo7b';
import { AuthThemeClient } from '@/components/tenant/theme/AuthThemeClient';
import { PricingPlans } from '@/components/public/pricing/PricingPlans';
import { MobileApps } from '@/components/public/pricing/MobileApps';
import { PlatformOrganizationJsonLd } from '@/components/public/PlatformOrganizationJsonLd';
import './pricing.css';

export default function PricingPage() {
  const [data, setData] = useState<BillingPlan[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // trialDays vem do PlatformConfig (SSOT). Fallback 30 se a chamada
  // falhar - alinhado com `auth-branding.ts` e `PlatformConfig.trialDays default`.
  const [trialDays, setTrialDays] = useState<number>(30);
  const isLoading = data === null && !error;

  useEffect(() => {
    billingApi
      .listPlans()
      .then((r) => setData(r.data))
      .catch(() => setError('Falha ao carregar planos.'));
    publicApi
      .getPlatformConfig()
      .then((r) => {
        if (typeof r.data?.trialDays === 'number' && r.data.trialDays > 0) {
          setTrialDays(r.data.trialDays);
        }
      })
      .catch(() => {
        /* mantem fallback 30 */
      });
  }, []);

  return (
    <div className="min-h-screen pricing-page">
      <PlatformOrganizationJsonLd />
      <AuthThemeClient />
      <header className="border-b border-[color:var(--border-default)] bg-[color:var(--surface-default)]/50 backdrop-blur-sm">
        <div className="pricing-header__inner">
          <Link href="/" aria-label="7barber - início">
            <Logo7b size="md" />
          </Link>
          <nav className="pricing-nav">
            <Link href="/login" className="btn btn--sm btn--ghost">
              <span>Entrar</span>
            </Link>
            <Link href="/register" className="btn btn--sm btn--primary">
              <span>Cadastrar</span>
            </Link>
          </nav>
        </div>
      </header>

      <main className="pricing-shell">
        <div className="pricing-hero">
          <span className="pricing-hero__eyebrow">
            <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
            Planos & preços
          </span>
          <h1 className="pricing-hero__title">
            O plano certo para a sua <em>barbearia</em>
          </h1>
          <p className="pricing-hero__lead">
            Do profissional autônomo à rede com várias unidades. Agenda, WhatsApp
            e chatbot com IA em um só lugar - você escala quando quiser.
          </p>
          <p className="pricing-trust">
            <ShieldCheck className="w-4 h-4 text-[color:var(--success)]" aria-hidden="true" />
            {trialDays} dias grátis em qualquer plano pago - sem cartão no trial.
          </p>
        </div>

        {isLoading && (
          <p className="text-center text-[color:var(--text-muted)]">
            Carregando planos...
          </p>
        )}
        {error && (
          <p className="text-center text-[color:var(--error)] text-sm">
            Falha ao carregar planos. Tente recarregar a página.
          </p>
        )}

        {data && <PricingPlans plans={data} trialDays={trialDays} />}

        <MobileApps />

        <div className="pricing-footnote">
          Pagamentos processados via Asaas - Pix, cartão e boleto. Troque de plano
          ou cancele quando quiser, direto pelo painel. Sem fidelidade.
        </div>
      </main>
    </div>
  );
}
